import { useEffect, useMemo, useState } from 'react'
import { api, type VocabularyEntry } from './api'
import {
  FACET_GROUPS,
  optionIsOn,
  rangeUnavailable,
  representableAsChip,
  unavailable,
  type Facet,
  type FacetOption,
} from './facets'

/**
 * Building a condition by picking, rather than by typing an expression.
 *
 * The `when` grammar has a CLOSED vocabulary — eleven bands with fixed values,
 * nine numbers taking a bound you choose, seventeen flags; 37 names in total —
 * and that is the only reason a builder is possible here at all. Until this
 * existed the only way to create a filter was a bare textarea placeholdered
 * with an example, so the tool was unusable by anybody who had not already
 * read `derive()`.
 *
 * Two things the picker fixes that a textarea cannot:
 *
 *   * A NAME THE ENGINE CANNOT EVALUATE. `language` is a fully documented band
 *     in variables.yaml that derive() never produces. `language == en` parses,
 *     so validate_when() accepts it, so it saves — and then the run dies
 *     part-way through a production CRM scan on expr._missing()'s deliberate
 *     KeyError. The vocabulary endpoint serves derive()'s own key set, so that
 *     condition is not expressible here.
 *
 *   * A VALUE THAT IS NOT A BAND. `traffic_band == deadd` matches nothing, ever,
 *     and reports as a segment that has simply gone quiet.
 *
 * WHAT IT DELIBERATELY DOES NOT DO: rewrite an expression it cannot represent.
 * A condition using `||` or grouping brackets is not a list of requirements and
 * flattening it into one would state something false — the same judgement
 * `explain.clauses()` makes server-side, where it returns `decomposable: false`
 * rather than a clause list. Those open in the raw editor with the reason said
 * out loud, and every condition keeps that editor available behind a toggle.
 */

export type BoundOp = '<=' | '>=' | '<' | '>'

export type Clause =
  | { kind: 'band'; name: string; op: '==' | '!=' | 'in'; values: string[] }
  | { kind: 'flag'; name: string; negated: boolean }
  /** A bound on a number — days since something, or a count over a window. */
  | { kind: 'number'; name: string; op: BoundOp; bound: number | null }
  /**
   * A bound on a CALENDAR DATE, which is a different thing from a day count
   * and deliberately a separate kind. `days_since_account_created <= 30` is a
   * window that moves with every day it runs; `account_created_on >=
   * 2026-01-01` is one that does not, and only the second can express "the Q1
   * cohort". Converting a picked range into day counts would slide it forward
   * daily, quietly re-aiming the filter.
   */
  | { kind: 'date'; name: string; op: BoundOp; on: string | null }

/** A row the operator has started and not finished. Not part of the output. */
export function incomplete(clause: Clause): boolean {
  if (clause.kind === 'band') return clause.values.length === 0
  // An empty box is not a bound. Zero IS one — `days_since_visit <= 0` is
  // "visited today" — so this tests for null rather than for falsiness.
  if (clause.kind === 'number') return clause.bound === null
  if (clause.kind === 'date') return !clause.on
  return false
}

/**
 * The periods people actually ask for, in days.
 *
 * "A day, a week, a month, three months, six months, a year" — the request
 * this feature came from, spelled out rather than left to arithmetic. The
 * months are calendar-ish approximations (30/90/180/365) because the signal
 * they bound IS an integer number of days: the SQL floors elapsed seconds to
 * days, so there is no more precision available to preserve.
 *
 * THREE SPELLINGS OF EACH, because English needs all three:
 *
 *   label   the button        "1 week"
 *   bare    after "the last"  "within the last week"
 *   phrase  standing alone    "more than a week ago"
 *
 * One form cannot serve them all — "within the last 1 week" and "within the
 * last a week" are both wrong, and they are what a single field produces.
 */
export const PERIODS: { days: number; label: string; bare: string; phrase: string }[] = [
  { days: 1, label: '1 day', bare: 'day', phrase: 'a day' },
  { days: 7, label: '1 week', bare: 'week', phrase: 'a week' },
  { days: 30, label: '1 month', bare: 'month', phrase: 'a month' },
  { days: 90, label: '3 months', bare: '3 months', phrase: '3 months' },
  { days: 180, label: '6 months', bare: '6 months', phrase: '6 months' },
  { days: 365, label: '12 months', bare: '12 months', phrase: '12 months' },
]

/**
 * The window presets, which are NOT the period presets, and start a week up.
 *
 * A one-day activity window matches nobody. The metric tables are written per
 * COMPLETED day — measured 2026-09-07, the newest row in both was the previous
 * day — so `now() - interval '1 day'` reached rows for ZERO apps out of the
 * whole base. Every windowed count would be NULL, no bound matches a NULL, and
 * the filter would select nobody while looking entirely well-formed.
 *
 * The server floor is two days (the shortest that reaches yesterday) and it
 * exists only to refuse the unmatchable case. A two-day window is still thin
 * and jittery, so nothing here offers one: the narrowest sensible answer to
 * "how much activity recently" is a week.
 *
 * The DAY-COUNT periods keep their `1 day` button, and that is not an
 * inconsistency — `days_since_visit` is computed from a live timestamp on the
 * account row, not from a per-day metric table, so "visited in the last day"
 * is a real and answerable question.
 */
export const WINDOW_PRESETS = PERIODS.filter((p) => p.days >= 7)

/** Mirrors crm.MIN_WINDOW_DAYS. The server refuses below this. */
export const MIN_WINDOW_DAYS = 2

/** `<= 7` on a backward-looking signal is "in the last week". */
export function readBound(
  op: BoundOp,
  bound: number,
  measure: 'days' | 'count' = 'days',
  direction: 'past' | 'future' = 'past',
): string {
  if (measure === 'count') {
    const words = { '<=': 'at most', '>=': 'at least', '<': 'fewer than', '>': 'more than' }
    return `${words[op]} ${bound.toLocaleString()}`
  }
  const period = PERIODS.find((p) => p.days === bound)
  const plain = `${bound.toLocaleString()} ${bound === 1 ? 'day' : 'days'}`
  const bare = period ? period.bare : plain
  const phrase = period ? period.phrase : plain
  const when = direction === 'future' ? 'next' : 'last'
  const ago = direction === 'future' ? 'away' : 'ago'
  // `<=` reads naturally as a window and takes the bare form. The other three
  // are spelled out rather than bent into one, because "within the last week"
  // and "at least a week ago" are opposite halves of the scale.
  if (op === '<=') return `within the ${when} ${bare}`
  if (op === '>') return `more than ${phrase} ${ago}`
  if (op === '<') return `less than ${phrase} ${ago}`
  return `at least ${phrase} ${ago}`
}

/**
 * Is this a date that exists?
 *
 * Round-tripped through Date rather than range-checked by hand, so leap years
 * come out right: 2024-02-29 is real and 2025-02-29 is not, and no amount of
 * "day <= 31" arithmetic knows the difference.
 */
export function isRealDate(iso: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false
  const parsed = new Date(`${iso}T00:00:00Z`)
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === iso
}

/** A date bound read as a requirement. A date is a point, not a quantity. */
export const DATE_READS: Record<BoundOp, string> = {
  '>=': 'on or after',
  '<=': 'on or before',
  '>': 'after',
  '<': 'before',
}

/**
 * One clause as the grammar spells it.
 *
 * The spacing is not cosmetic: `explain.clauses()` parses saved conditions back
 * with IN_RE / CMP_RE / FLAG_RE, and the Filter brief describes what those
 * produce. Serialising differently would give a condition that means the same
 * thing to the evaluator and reads differently on the screen that explains it.
 */
export function serializeClause(clause: Clause): string {
  if (clause.kind === 'flag') return clause.negated ? `!${clause.name}` : clause.name
  if (clause.kind === 'number') return `${clause.name} ${clause.op} ${clause.bound}`
  if (clause.kind === 'date') return `${clause.name} ${clause.op} ${clause.on}`
  if (clause.op === 'in') return `${clause.name} in (${clause.values.join(', ')})`
  return `${clause.name} ${clause.op} ${clause.values[0]}`
}

/** The whole condition. Empty means catch-all, which is a real saved state. */
export function serialize(clauses: Clause[]): string {
  return clauses.filter((c) => !incomplete(c)).map(serializeClause).join(' && ')
}

// The three clause shapes, matched exactly as explain.py matches them.
const IN_RE = /^\s*([A-Za-z_][A-Za-z0-9_]*)\s+in\s*\((.*)\)\s*$/
const CMP_RE = /^\s*([A-Za-z_][A-Za-z0-9_]*)\s*(==|!=)\s*([A-Za-z0-9_]+)\s*$/
const NUM_RE = /^\s*([A-Za-z_][A-Za-z0-9_]*)\s*(<=|>=|<|>)\s*(\d+)\s*$/
// Tried BEFORE NUM_RE. `\d+` cannot swallow the hyphens so they do not
// actually collide, but the order is stated so it stays that way.
const DATE_RE = /^\s*([A-Za-z_][A-Za-z0-9_]*)\s*(<=|>=|<|>)\s*(\d{4}-\d{2}-\d{2})\s*$/
const FLAG_RE = /^\s*(!?)\s*([A-Za-z_][A-Za-z0-9_]*)\s*$/

/** `||` or grouping brackets. Membership's own brackets are not grouping. */
const MEMBERSHIP = /\bin\s*\([^()]*\)/g
const NOT_A_FLAT_AND = /\|\||[()]/

/**
 * A saved condition as clauses, or null if the builder cannot represent it.
 *
 * Null on ANY doubt: an unrecognised shape, a name outside the vocabulary, a
 * value that is not one of the band's. Returning a partial reading would let a
 * save silently drop the part that was not understood.
 */
export function parseWhen(when: string | null, vocab: VocabularyEntry[]): Clause[] | null {
  const text = (when ?? '').trim()
  if (!text) return []
  if (NOT_A_FLAT_AND.test(text.replace(MEMBERSHIP, 'in'))) return null

  const byName = new Map(vocab.map((v) => [v.name, v]))
  const out: Clause[] = []

  for (const part of text.split('&&')) {
    // AN EMPTY PART IS A DANGLING `&&`, NOT NOTHING. Skipping it silently
    // repaired `commercial_state == paying &&` into a valid one-clause
    // condition — but compile_when() REJECTS that string ("unexpected end of
    // expression"), so the builder would have been quietly fixing an
    // expression the evaluator refuses. Refusing it keeps the half-typed
    // clause in the raw editor, where the 422 says what is wrong with it.
    if (!part.trim()) return null

    const membership = IN_RE.exec(part)
    if (membership) {
      const entry = byName.get(membership[1])
      const values = membership[2].split(',').map((v) => v.trim()).filter(Boolean)
      if (!entry || entry.kind !== 'band' || !values.length) return null
      if (values.some((v) => !entry.values?.some((b) => b.value === v))) return null
      out.push({ kind: 'band', name: entry.name, op: 'in', values })
      continue
    }

    const on = DATE_RE.exec(part)
    if (on) {
      const entry = byName.get(on[1])
      if (!entry || entry.kind !== 'date') return null
      // SHAPE IS NOT VALIDITY. `2026-13-45` matches the pattern and is not a
      // date; compared as text it would sort after every real one and behave
      // as the far future. A date picker cannot produce it, the raw editor can.
      if (!isRealDate(on[3])) return null
      out.push({ kind: 'date', name: entry.name, op: on[2] as BoundOp, on: on[3] })
      continue
    }

    const bound = NUM_RE.exec(part)
    if (bound) {
      const entry = byName.get(bound[1])
      if (!entry || entry.kind !== 'number') return null
      out.push({
        kind: 'number',
        name: entry.name,
        op: bound[2] as BoundOp,
        bound: Number(bound[3]),
      })
      continue
    }

    const comparison = CMP_RE.exec(part)
    if (comparison) {
      const entry = byName.get(comparison[1])
      if (!entry || entry.kind !== 'band') return null
      if (!entry.values?.some((b) => b.value === comparison[3])) return null
      out.push({
        kind: 'band',
        name: entry.name,
        op: comparison[2] as '==' | '!=',
        values: [comparison[3]],
      })
      continue
    }

    const flag = FLAG_RE.exec(part)
    if (flag) {
      const entry = byName.get(flag[2])
      if (!entry || entry.kind !== 'flag') return null
      out.push({ kind: 'flag', name: entry.name, negated: flag[1] === '!' })
      continue
    }

    return null
  }

  return out
}

/**
 * Why the builder cannot take this expression — in terms of THIS text.
 *
 * The first version explained only the `||` case and only for a condition that
 * arrived already saved, so pressing "Back to the builder" on a hand-typed
 * expression with an unknown name did nothing and said nothing: the button
 * looked broken rather than the expression looking wrong.
 */
export function refusalReason(when: string): string {
  if (NOT_A_FLAT_AND.test(when.replace(MEMBERSHIP, 'in'))) {
    return (
      'This uses `||` or brackets, so its parts are alternatives rather than a list of ' +
      'requirements — the builder would have to flatten it into a list claiming all of them ' +
      'are required. Shown as written.'
    )
  }
  return (
    'This names something the builder does not recognise — a name outside the 28 the engine ' +
    'produces, or a value that is not one of the band’s. Shown as written; a name the engine ' +
    'cannot evaluate saves cleanly and then fails part-way through a scan.'
  )
}

/** `has_verified_email` -> `has verified email`. The names were written to read. */
function words(name: string): string {
  return name.replace(/_/g, ' ')
}

/** One clause as the requirement it places on an account. */
export function readClause(clause: Clause, entry?: VocabularyEntry): string {
  if (clause.kind === 'flag') {
    return clause.negated ? `NOT ${words(clause.name)}` : words(clause.name)
  }
  const name = words(clause.name)
  if (clause.kind === 'number') {
    if (clause.bound === null) return name
    const phrase = readBound(clause.op, clause.bound, entry?.measure, entry?.direction)
    return entry?.measure === 'count' ? `${name} is ${phrase}` : `${name}: ${phrase}`
  }
  if (clause.kind === 'date') {
    if (!clause.on) return name
    // The trailing "on" comes off the NAME, because every date signal is
    // spelled `…_on` and the reading starts with "on or after" — leaving both
    // produced "account created on on or after 2026-01-01".
    const subject = name.replace(/ on$/, '')
    // "on or after", not "at least" — a date is a point, and "at least
    // 2026-01-01" reads as a quantity of dates.
    return `${subject} ${DATE_READS[clause.op]} ${clause.on}`
  }
  if (clause.op === 'in') return `${name} is one of ${clause.values.join(', ')}`
  return `${name} is ${clause.op === '!=' ? 'not ' : ''}${clause.values[0]}`
}

/**
 * The vocabulary, fetched once per session.
 *
 * It describes the engine's key set, not any account, so it cannot go stale
 * while the tab is open — and both call sites in Filters render at the same
 * time, so a shared promise stops the second one refetching 28 rows nobody
 * changed.
 */
let pending: Promise<VocabularyEntry[]> | null = null

export function useVocabulary(): { vocab: VocabularyEntry[] | null; error: string | null } {
  const [vocab, setVocab] = useState<VocabularyEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    pending ??= api.vocabulary()
    pending
      .then((v) => live && setVocab(v))
      .catch((e) => {
        // Cleared so a transient failure does not poison the cache for the
        // rest of the session — the next mount retries.
        pending = null
        if (live) setError(String((e as Error).message))
      })
    return () => {
      live = false
    }
  }, [])
  return { vocab, error }
}

/**
 * The condition editor.
 *
 * UNCONTROLLED on purpose. The builder owns the draft; the parent owns what is
 * saved. A row that has been started and not finished — a band with no value
 * chosen — has no expression to be the controlled value of, and driving this
 * from a string would make that state unrepresentable and the row vanish under
 * the operator mid-edit.
 *
 * `initial` is read once. Both call sites already remount on identity change
 * (FilterDetail is keyed on the filter name), so there is no second source of
 * truth to reconcile.
 */
export function ClauseBuilder({
  initial,
  initialWindow = null,
  catchAll = false,
  disabled = false,
  onChange,
  onWindowChange,
}: {
  initial: string | null
  /** The stored activity window, if this filter has one. */
  initialWindow?: number | null
  /** The catch-all's empty condition is deliberate, not an unfinished edit. */
  catchAll?: boolean
  disabled?: boolean
  onChange: (when: string | null) => void
  /** Reported separately from the condition because it is stored separately —
   *  and because either can be edited without the other. */
  onWindowChange?: (days: number | null) => void
}) {
  const { vocab, error } = useVocabulary()
  const [clauses, setClauses] = useState<Clause[] | null>(null)
  const [raw, setRaw] = useState(initial ?? '')
  // 'raw' until the vocabulary arrives and says the condition is buildable.
  // Starting in 'build' would show an empty builder over a saved condition for
  // as long as the fetch takes, which reads as a condition that has been lost.
  const [mode, setMode] = useState<'build' | 'raw'>('raw')
  // Why the builder is not available, in terms of the text that was refused.
  const [refusal, setRefusal] = useState<string | null>(null)
  // Folded away by default, and OPENED WHEN THE CHIPS CANNOT SHOW EVERYTHING.
  // A saved condition containing something no chip represents would otherwise
  // be invisible: the chips would all read `any`, which says the filter has no
  // requirements when it plainly has some.
  const [advanced, setAdvanced] = useState(false)

  useEffect(() => {
    if (!vocab) return
    const parsed = parseWhen(initial, vocab)
    if (parsed) {
      setClauses(parsed)
      setMode('build')
      setAdvanced(parsed.some((c) => !representableAsChip(c, serializeClause)))
    } else {
      setRefusal(refusalReason(initial ?? ''))
    }
  }, [vocab, initial])

  const commit = (next: Clause[]) => {
    setClauses(next)
    const when = serialize(next)
    setRaw(when)
    onChange(when || null)
  }

  if (error) {
    // The raw editor still works without the vocabulary, so the fetch failing
    // costs the picker and nothing else.
    return (
      <>
        <div className="error">Could not load the vocabulary: {error}</div>
        <RawEditor
          value={raw}
          disabled={disabled}
          onChange={(v) => {
            setRaw(v)
            onChange(v.trim() || null)
          }}
        />
      </>
    )
  }

  if (mode === 'raw') {
    return (
      <div className="stack">
        {refusal && <p className="hint">{refusal}</p>}
        {!vocab && <div className="loading">Loading the vocabulary…</div>}
        <RawEditor
          value={raw}
          disabled={disabled}
          onChange={(v) => {
            setRaw(v)
            // The reason described the text that was refused. Once that text
            // has been edited it may no longer be true of what is on screen.
            setRefusal(null)
            onChange(v.trim() || null)
          }}
        />
        {vocab && (
          <div className="row">
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                const parsed = parseWhen(raw, vocab)
                if (parsed) {
                  setClauses(parsed)
                  setRefusal(null)
                  setMode('build')
                } else {
                  setRefusal(refusalReason(raw))
                }
              }}
            >
              ↩ Back to the builder
            </button>
            <span className="hint">
              Only if what is typed above is a plain <span className="mono">&amp;&amp;</span> chain
              of known names. Nothing here rewrites an expression it cannot represent.
            </span>
          </div>
        )}
      </div>
    )
  }

  const rows = clauses ?? []
  const complete = rows.filter((c) => !incomplete(c))
  const byName = new Map((vocab ?? []).map((v) => [v.name, v]))
  // Does anything here count activity over the filter's window? If so the
  // window has to be set, and the API refuses the save without it.
  const needsWindow = rows.some((c) => byName.get(c.name)?.windowed)

  return (
    <div className="stack">
      {/* CHIPS FIRST, and the field/operator/value builder second and folded
          away. The builder can say anything the engine can evaluate, which is
          exactly what made it hard: three decisions before you have expressed
          one idea. Most conditions are a handful of common facts, so those get
          one click each and the general tool waits behind a toggle. */}
      <QuickFilters
        clauses={rows}
        vocab={vocab!}
        disabled={disabled}
        onChange={commit}
      />

      <div className="row">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setAdvanced(!advanced)}
        >
          {advanced ? '▾' : '▸'} Advanced
        </button>
        <span className="hint">
          {rows.length === 0
            ? 'Every name the engine has, with any comparison — for what the chips do not cover.'
            : `${rows.length} requirement${rows.length === 1 ? '' : 's'}, editable one by one.`}
        </span>
        {rows.length > 0 && (
          <button type="button" className="linkish" disabled={disabled} onClick={() => commit([])}>
            clear all
          </button>
        )}
      </div>

      {advanced && (
        <>
          <div className="builder">
            {rows.length === 0 ? (
              <div className="empty" style={{ padding: 14 }}>
                {catchAll
                  ? 'No requirements — the catch-all takes whatever fell through every filter above it.'
                  : 'No requirements yet, so this would match every account in the mailable base.'}
              </div>
            ) : (
              rows.map((clause, index) => (
                <ClauseRow
                  key={index}
                  clause={clause}
                  vocab={vocab!}
                  first={index === 0}
                  disabled={disabled}
                  onChange={(next) => commit(rows.map((c, i) => (i === index ? next : c)))}
                  onRemove={() => commit(rows.filter((_, i) => i !== index))}
                />
              ))
            )}
          </div>

          <div className="row">
            <AddClause
              vocab={vocab!}
              // Dates excluded from "already used": a range is legitimately
              // TWO clauses on one name (`>= from` and `<= to`), so disabling
              // the name after the first made the second unbuildable here.
              used={rows.filter((c) => c.kind !== 'date').map((c) => c.name)}
              disabled={disabled}
              onAdd={(clause) => commit([...rows, clause])}
            />
            <button
              type="button"
              className="linkish"
              disabled={disabled}
              onClick={() => {
                setRaw(serialize(rows))
                setMode('raw')
              }}
            >
              Edit as expression
            </button>
          </div>
        </>
      )}

      {needsWindow && (
        <WindowControl
          value={initialWindow}
          disabled={disabled}
          onChange={(days) => onWindowChange?.(days)}
        />
      )}

      <Readback clauses={complete} vocab={vocab ?? []} catchAll={catchAll} />
    </div>
  )
}

/**
 * How long "in window" is, when a condition counts activity over one.
 *
 * A SEPARATE SETTING FROM THE CONDITION, and it has to be. The window is one
 * number for the whole filter, and it changes the meaning of every windowed
 * clause in it at once — so it belongs beside the condition rather than inside
 * a clause, where two clauses could disagree about it.
 *
 * The costs stated here are the reason it is not simply always on: the
 * aggregates behind it are the most expensive part of the signal query, and
 * `crm.build_signal_sql()` removes them entirely from every run whose filters
 * do not name them.
 */
function WindowControl({
  value,
  disabled,
  onChange,
}: {
  value: number | null
  disabled?: boolean
  onChange: (days: number | null) => void
}) {
  const [days, setDays] = useState<number | null>(value)
  const set = (next: number | null) => {
    setDays(next)
    onChange(next)
  }

  return (
    <div className={days === null ? 'error' : 'notice'}>
      <div className="hint label-line" style={{ color: 'inherit' }}>
        <strong>Activity window</strong> — how far back the counts above reach.
        {days === null && ' Required: a count over a window means nothing without one.'}
      </div>
      <div className="row" style={{ gap: 6 }}>
        {WINDOW_PRESETS.map((period) => (
          <button
            key={period.days}
            type="button"
            className={days === period.days ? 'primary' : undefined}
            disabled={disabled}
            onClick={() => set(period.days)}
          >
            {period.label}
          </button>
        ))}
        <input
          type="number"
          min={MIN_WINDOW_DAYS}
          max={3650}
          value={days ?? ''}
          disabled={disabled}
          style={{ width: 90 }}
          placeholder="days"
          onChange={(e) =>
            set(
              e.target.value.trim() === ''
                ? null
                : Math.max(MIN_WINDOW_DAYS, Number(e.target.value)),
            )
          }
        />
      </div>
      <div className="hint caption">
        Nothing shorter than a week is offered, and the server refuses under{' '}
        {MIN_WINDOW_DAYS} days: the activity tables are written per completed day, so a
        one-day window reaches no rows at all and the filter would select nobody while
        looking perfectly well-formed.
      </div>
      <div className="hint caption">
        This is the one window for the whole filter, and two filters that run
        together must agree on it — one scan produces one window, so a run whose
        filters disagree is refused rather than silently given one of them.
        {' '}<strong>Measured 2026-09-07:</strong> a month ≈2min, 3 months ≈5min, 6 months
        ≈5½min, 12 months ≈7½min. Most of that is the base query, which every scan pays, so
        widening costs less than proportionally — but all of it is over the 5-minute cap on{' '}
        <strong>Preview count</strong>, so a windowed filter has to be counted by starting a
        real run. Use <strong>Cancel</strong> rather than waiting one out. These are the most
        expensive aggregates in the signal query and are skipped entirely for filters that do
        not use them.
      </div>
    </div>
  )
}

/**
 * The chips, and the whole reason this screen is usable without reading
 * `derive()`.
 *
 * A chip TOGGLES A CLAUSE in the same list Advanced renders — it is not a
 * parallel state to be folded in later, which is where v1's version of this
 * got complicated. So the effect of a click is visible twice, once as the chip
 * lighting up and once as a row appearing below, and the two cannot disagree.
 *
 * `one` rows are either/or: picking replaces whatever else in the row was
 * chosen, and `any` clears the row. `many` rows accumulate — several values of
 * one band fold into a single `in` clause, because that is what "stalled at
 * any of these" means, while independent yes/no chips each add their own.
 */
function QuickFilters({
  clauses,
  vocab,
  disabled,
  onChange,
}: {
  clauses: Clause[]
  vocab: VocabularyEntry[]
  disabled?: boolean
  onChange: (clauses: Clause[]) => void
}) {
  /**
   * Which questions are expanded.
   *
   * Six groups of chips is a tall form, so a group opens only when it has
   * something in it — a new filter shows six headings, each a question, and
   * you open the ones you want. `null` means "nobody has clicked this header",
   * which is when the selection count decides; an explicit true/false is the
   * operator overriding that and must survive a re-render.
   */
  const [opened, setOpened] = useState<Record<string, boolean>>({})
  const isOpen = (title: string, hasChoices: boolean) =>
    opened[title] ?? hasChoices
  const setOpen = (title: string, next: boolean) =>
    setOpened((current) => ({ ...current, [title]: next }))

  /** Drop every clause this facet's options could have added. */
  const withoutFacet = (list: Clause[], facet: Facet): Clause[] => {
    const owned = new Set(
      facet.options.map((o) => serializeClause(o.clause)),
    )
    // A `many` band facet folds into one `in` clause, which serialises to
    // none of the single-value forms above — so the whole name goes too when
    // the facet owns it. Names are checked rather than expressions for that
    // reason.
    const names = new Set(facet.options.map((o) => o.clause.name))
    return list.filter((c) => {
      if (owned.has(serializeClause(c))) return false
      if (facet.kind === 'many' && c.kind === 'band' && names.has(c.name)) return false
      if (facet.kind === 'one' && names.has(c.name)) return false
      return true
    })
  }

  /** The values of `name` currently selected, across `==` and `in`. */
  const selectedValues = (name: string): string[] => {
    const found = clauses.find((c) => c.kind === 'band' && c.name === name)
    return found && found.kind === 'band' ? found.values : []
  }

  /**
   * Is this chip lit?
   *
   * FACET-AWARE, because a `many` band row folds its values into ONE clause.
   * Comparing serialised forms alone — which is right for every other chip —
   * left `quota_pressure in (over, approaching)` showing both its chips as
   * unselected, so a saved filter's own settings were invisible and clicking
   * `Over` would have silently replaced the pair with a single value.
   */
  const chipIsOn = (facet: Facet, option: FacetOption): boolean => {
    if (facet.kind === 'many' && option.clause.kind === 'band' && option.clause.op === '==') {
      return selectedValues(option.clause.name).includes(option.clause.values[0])
    }
    return optionIsOn(option, clauses, serializeClause)
  }

  const toggle = (facet: Facet, option: FacetOption) => {
    const on = chipIsOn(facet, option)
    const picked = option.clause

    if (facet.kind === 'many' && picked.kind === 'band' && picked.op === '==') {
      // Several values of one band, folded. `==` for exactly one and `in` for
      // more, which is what a person would write and what the advanced
      // builder produces — so a chip and a hand-built row are the same string.
      const name = picked.name
      const value = picked.values[0]
      const entry = vocab.find((v) => v.name === name)
      const current = new Set(selectedValues(name))
      if (current.has(value)) current.delete(value)
      else current.add(value)
      const kept = clauses.filter((c) => !(c.kind === 'band' && c.name === name))
      if (current.size === 0) return onChange(kept)
      // Declared band order, not click order: `in (high, dead)` reads as a
      // range and is not one.
      const ordered = (entry?.values ?? [])
        .map((b) => b.value)
        .filter((v) => current.has(v))
      return onChange([
        ...kept,
        {
          kind: 'band',
          name,
          op: ordered.length === 1 ? '==' : 'in',
          values: ordered,
        },
      ])
    }

    if (facet.kind === 'many') {
      // Independent chips: each is its own clause.
      return onChange(
        on
          ? clauses.filter((c) => serializeClause(c) !== serializeClause(option.clause))
          : [...clauses, option.clause],
      )
    }

    // Either/or.
    const cleared = withoutFacet(clauses, facet)
    return onChange(on ? cleared : [...cleared, option.clause])
  }

  /** Nothing in this row chosen — the resting state, not a choice. */
  const anyOn = (facet: Facet) => !facet.options.some((o) => chipIsOn(facet, o))

  return (
    <div className="facets">
      {FACET_GROUPS.map((group) => {
        const facets = group.facets
          .map((f) => ({
            ...f,
            options: f.options.filter((o) => !unavailable(o, vocab)),
          }))
          // A range row carries no options — it is two date boxes — so it is
          // kept or dropped on whether its date signal exists, not on how many
          // chips survived. Filtering on option count alone deleted every one.
          .filter((f) =>
            f.kind === 'range' ? !rangeUnavailable(f, vocab) : f.options.length > 0,
          )
        if (!facets.length) return null
        // How many requirements this group is currently contributing. Shown on
        // the header so a COLLAPSED group with selections in it cannot look
        // like an untouched one — which is the only way collapsing these can
        // hide something from the operator.
        const chosen = facets.reduce(
          (n, f) =>
            n +
            (f.kind === 'range'
              ? clauses.filter((c) => c.kind === 'date' && c.name === f.field).length
              : f.options.filter((o) => chipIsOn(f, o)).length),
          0,
        )
        const open = isOpen(group.title, chosen > 0)
        return (
          <div key={group.title} className="facet-group">
            <button
              type="button"
              className="facet-head"
              aria-expanded={open}
              onClick={() => setOpen(group.title, !open)}
            >
              <span className="facet-caret">{open ? '▾' : '▸'}</span>
              <h4>{group.title}</h4>
              {chosen > 0 && <span className="badge running">{chosen}</span>}
            </button>
            {open && facets.map((facet) => (
              <div key={facet.id} className="facet">
                {facet.label && <span className="facet-label">{facet.label}</span>}
                {facet.kind === 'range' ? (
                  <DateRange
                    field={facet.field!}
                    clauses={clauses}
                    disabled={disabled}
                    onChange={onChange}
                  />
                ) : (
                <span className="chip-row">
                  {facet.kind === 'one' && (
                    <button
                      type="button"
                      className={`chip any${anyOn(facet) ? ' on' : ''}`}
                      disabled={disabled}
                      onClick={() => onChange(withoutFacet(clauses, facet))}
                    >
                      any
                    </button>
                  )}
                  {facet.options.map((option) => (
                    <button
                      key={option.label}
                      type="button"
                      title={option.hint}
                      className={`chip${chipIsOn(facet, option) ? ' on' : ''}`}
                      disabled={disabled}
                      onClick={() => toggle(facet, option)}
                    >
                      {option.label}
                    </button>
                  ))}
                </span>
                )}
              </div>
            ))}
            {open && facets.some((f) => f.hint) && (
              <p className="hint facet-hint">
                {facets.map((f) => f.hint).filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
        )
      })}
    </div>
  )
}

/**
 * Two calendar dates on one signal — a fixed range, not a rolling window.
 *
 * ONE ROW, TWO CLAUSES. `account_created_on >= 2026-01-01 && account_created_on
 * <= 2026-06-30` is two requirements to the engine and one idea to a person, so
 * this reads and writes the pair while presenting one control. Either half may
 * be empty, which is an open-ended range and a real thing to ask for — "signed
 * up before April" needs no lower bound.
 *
 * The dates are stored as written. Nothing here converts them into "180 to 330
 * days ago", which is the tempting shortcut and would make the filter mean
 * something different every day it ran.
 */
function DateRange({
  field,
  clauses,
  disabled,
  onChange,
}: {
  field: string
  clauses: Clause[]
  disabled?: boolean
  onChange: (clauses: Clause[]) => void
}) {
  const bound = (op: BoundOp) => {
    const found = clauses.find(
      (c) => c.kind === 'date' && c.name === field && c.op === op,
    )
    return found && found.kind === 'date' ? (found.on ?? '') : ''
  }
  const from = bound('>=')
  const to = bound('<=')

  const set = (op: BoundOp, value: string) => {
    const others = clauses.filter(
      (c) => !(c.kind === 'date' && c.name === field && c.op === op),
    )
    if (!value) return onChange(others)
    // Appended in a stable place rather than pushed to the end each time, so
    // editing the `from` box does not reorder the saved expression.
    const next: Clause = { kind: 'date', name: field, op, on: value }
    const at = clauses.findIndex(
      (c) => c.kind === 'date' && c.name === field && c.op === op,
    )
    if (at === -1) return onChange([...clauses, next])
    return onChange(clauses.map((c, i) => (i === at ? next : c)))
  }

  // A backwards range matches nobody and is worth saying so, not silently
  // saving — the two boxes are easy to fill in the wrong order.
  const backwards = !!from && !!to && from > to

  return (
    <span className="row" style={{ gap: 6 }}>
      <input
        type="date"
        value={from}
        disabled={disabled}
        aria-label={`${field} from`}
        onChange={(e) => set('>=', e.target.value)}
      />
      <span className="dim">to</span>
      <input
        type="date"
        value={to}
        disabled={disabled}
        aria-label={`${field} to`}
        onChange={(e) => set('<=', e.target.value)}
      />
      {(from || to) && (
        <button
          type="button"
          className="linkish"
          disabled={disabled}
          onClick={() =>
            onChange(clauses.filter((c) => !(c.kind === 'date' && c.name === field)))
          }
        >
          clear
        </button>
      )}
      {backwards && (
        <span className="badge bad" title="no account can be created after the end and before the start">
          end is before the start
        </span>
      )}
      {!from && !to && <span className="hint">any date</span>}
    </span>
  )
}

function RawEditor({
  value,
  disabled,
  onChange,
}: {
  value: string
  disabled?: boolean
  onChange: (value: string) => void
}) {
  return (
    <div>
      <textarea
        rows={3}
        value={value}
        disabled={disabled}
        spellCheck={false}
        onChange={(e) => onChange(e.target.value)}
        placeholder="commercial_state == never_paid && has_verified_email"
      />
      <div className="hint caption">
        Rejected on save if it does not parse. A name the engine does not
        produce parses fine and then fails mid-scan, which is what the builder
        prevents and this box does not.
      </div>
    </div>
  )
}

/**
 * One requirement, and the choices it has.
 *
 * A band's values carry their cut-off — `dead — < 200` — because the band name
 * alone is a word: `traffic_band == dead` only means something once "dead" is
 * "under 200 visits in 30 days". Those rules come from the same variables.yaml
 * the evaluator's thresholds do, so the label cannot drift from the test.
 */
function ClauseRow({
  clause,
  vocab,
  first,
  disabled,
  onChange,
  onRemove,
}: {
  clause: Clause
  vocab: VocabularyEntry[]
  first: boolean
  disabled?: boolean
  onChange: (clause: Clause) => void
  onRemove: () => void
}) {
  const [why, setWhy] = useState(false)
  const entry = vocab.find((v) => v.name === clause.name)

  return (
    <div className="builder-row">
      <div className="builder-join">{first ? 'WHERE' : 'AND'}</div>
      <div className="builder-clause">
        <div className="row" style={{ gap: 8 }}>
          <span className="mono clause-name">{clause.name}</span>
          {clause.kind === 'band' ? (
            <select
              value={clause.op}
              disabled={disabled}
              style={{ width: 'auto' }}
              onChange={(e) => {
                const op = e.target.value as '==' | '!=' | 'in'
                onChange({
                  ...clause,
                  op,
                  // `==` and `!=` take exactly one value. Dropping the extras
                  // rather than keeping them invisible: a hidden second value
                  // that reappears on switching back is a condition changing
                  // without anybody editing it.
                  values: op === 'in' ? clause.values : clause.values.slice(0, 1),
                })
              }}
            >
              <option value="==">is</option>
              <option value="!=">is not</option>
              <option value="in">is one of</option>
            </select>
          ) : clause.kind === 'date' ? (
            <select
              value={clause.op}
              disabled={disabled}
              style={{ width: 'auto' }}
              onChange={(e) => onChange({ ...clause, op: e.target.value as BoundOp })}
            >
              <option value=">=">on or after</option>
              <option value="<=">on or before</option>
              <option value=">">after</option>
              <option value="<">before</option>
            </select>
          ) : clause.kind === 'number' ? (
            <select
              value={clause.op}
              disabled={disabled}
              style={{ width: 'auto' }}
              onChange={(e) => onChange({ ...clause, op: e.target.value as BoundOp })}
            >
              {entry?.measure === 'count' ? (
                <>
                  <option value=">">more than</option>
                  <option value=">=">at least</option>
                  <option value="<">fewer than</option>
                  <option value="<=">at most</option>
                </>
              ) : (
                <>
                  <option value="<=">
                    within the {entry?.direction === 'future' ? 'next' : 'last'}
                  </option>
                  <option value=">">
                    more than … {entry?.direction === 'future' ? 'away' : 'ago'}
                  </option>
                  <option value=">=">
                    at least … {entry?.direction === 'future' ? 'away' : 'ago'}
                  </option>
                  <option value="<">
                    less than … {entry?.direction === 'future' ? 'away' : 'ago'}
                  </option>
                </>
              )}
            </select>
          ) : (
            <select
              value={clause.negated ? 'false' : 'true'}
              disabled={disabled}
              style={{ width: 'auto' }}
              onChange={(e) => onChange({ ...clause, negated: e.target.value === 'false' })}
            >
              <option value="true">is true</option>
              <option value="false">is false</option>
            </select>
          )}
          <span style={{ flex: 1 }} />
          {entry?.note && (
            <button type="button" className="linkish" onClick={() => setWhy(!why)}>
              {why ? 'hide why' : 'why'}
            </button>
          )}
          <button type="button" className="linkish" disabled={disabled} onClick={onRemove}>
            remove
          </button>
        </div>

        {clause.kind === 'band' && entry?.values && (
          <div className="builder-values">
            {entry.values.map((band) => {
              const on = clause.values.includes(band.value)
              return (
                <label key={band.value} className="check builder-value">
                  <input
                    type={clause.op === 'in' ? 'checkbox' : 'radio'}
                    style={{ width: 'auto' }}
                    name={`${clause.name}-value`}
                    checked={on}
                    disabled={disabled}
                    onChange={() => {
                      if (clause.op !== 'in') {
                        onChange({ ...clause, values: [band.value] })
                        return
                      }
                      onChange({
                        ...clause,
                        values: on
                          ? clause.values.filter((v) => v !== band.value)
                          : // Kept in the band's own declared order, which is
                            // the ordinal ladder. Append order would print
                            // `in (high, dead)` and read as a range.
                            entry.values!
                              .map((b) => b.value)
                              .filter((v) => v === band.value || clause.values.includes(v)),
                      })
                    }}
                  />
                  <span className="mono">{band.value}</span>
                  <span className="dim"> — {band.rule}</span>
                </label>
              )
            })}
          </div>
        )}

        {clause.kind === 'number' && (
          <BoundControl clause={clause} entry={entry} disabled={disabled} onChange={onChange} />
        )}

        {clause.kind === 'date' && (
          <div className="builder-values">
            <input
              type="date"
              value={clause.on ?? ''}
              disabled={disabled}
              onChange={(e) => onChange({ ...clause, on: e.target.value || null })}
            />
            <span className="hint">a fixed date — it does not move as time passes</span>
          </div>
        )}

        {incomplete(clause) && (
          <div className="hint caption" style={{ color: 'var(--warn)' }}>
            Nothing chosen, so this requirement is not part of the condition yet.
          </div>
        )}

        {/* WHAT NULL MEANS, on the row, not in a tooltip. No bound matches a
            NULL in either direction, so `days_since_banner_customized <= 30`
            silently excludes every account that never customised anything —
            which reads as "customised recently" and is not what it does.
            The signal's own note says which population it is and how to
            select it instead. */}
        {(clause.kind === 'number' || clause.kind === 'date') && entry?.null_means && (
          <div className="clause-rule" style={{ color: 'var(--warn)' }}>
            No period matches “{entry.null_means.trim()}”
          </div>
        )}

        {entry?.source && (
          <div className="clause-rule dim">
            from <span className="mono">{entry.source}</span>
            {entry.unit ? <span> ({entry.unit})</span> : null}
          </div>
        )}
        {why && entry?.note && <pre className="notes">{entry.note}</pre>}
      </div>
    </div>
  )
}

/**
 * The number a bound compares against.
 *
 * Two different controls behind one clause kind, because the bound measures
 * two different things. A day count gets the periods somebody actually asks
 * for — a day, a week, a month, three, six, twelve — as buttons, with a box
 * for anything else. A windowed COUNT gets a plain number, because "3 months"
 * is not a quantity of visits; the period for those lives on the filter, as
 * its window.
 */
function BoundControl({
  clause,
  entry,
  disabled,
  onChange,
}: {
  clause: Extract<Clause, { kind: 'number' }>
  entry?: VocabularyEntry
  disabled?: boolean
  onChange: (clause: Clause) => void
}) {
  const isCount = entry?.measure === 'count'
  const [custom, setCustom] = useState(
    clause.bound !== null && !PERIODS.some((p) => p.days === clause.bound),
  )

  if (isCount) {
    return (
      <div className="builder-values">
        <input
          type="number"
          min={0}
          value={clause.bound ?? ''}
          disabled={disabled}
          style={{ width: 120 }}
          placeholder="how many"
          onChange={(e) =>
            onChange({
              ...clause,
              bound: e.target.value.trim() === '' ? null : Math.max(0, Number(e.target.value)),
            })
          }
        />
        <span className="hint">{entry?.unit ?? 'count'}</span>
      </div>
    )
  }

  return (
    <div className="builder-values">
      {PERIODS.map((period) => (
        <button
          key={period.days}
          type="button"
          className={clause.bound === period.days && !custom ? 'primary' : undefined}
          disabled={disabled}
          onClick={() => {
            setCustom(false)
            onChange({ ...clause, bound: period.days })
          }}
        >
          {period.label}
        </button>
      ))}
      <button
        type="button"
        className={custom ? 'primary' : undefined}
        disabled={disabled}
        onClick={() => setCustom(true)}
      >
        custom
      </button>
      {custom && (
        <span className="row" style={{ gap: 6 }}>
          <input
            type="number"
            min={0}
            value={clause.bound ?? ''}
            disabled={disabled}
            autoFocus
            style={{ width: 90 }}
            onChange={(e) =>
              onChange({
                ...clause,
                bound: e.target.value.trim() === '' ? null : Math.max(0, Number(e.target.value)),
              })
            }
          />
          <span className="hint">days</span>
        </span>
      )}
    </div>
  )
}

/**
 * Adding a requirement.
 *
 * Bands and flags are separate groups because they compose differently — a
 * band is a comparison, a flag is a condition, which is the distinction
 * flags.yaml's own header opens with. Names already used are disabled rather
 * than hidden: two clauses on one band is almost always a mistake (`traffic_band
 * == dead && traffic_band == low` can never match), and saying so is more use
 * than a list that quietly shrinks.
 */
function AddClause({
  vocab,
  used,
  disabled,
  onAdd,
}: {
  vocab: VocabularyEntry[]
  used: string[]
  disabled?: boolean
  onAdd: (clause: Clause) => void
}) {
  const [choice, setChoice] = useState('')
  const bands = useMemo(() => vocab.filter((v) => v.kind === 'band'), [vocab])
  const numbers = useMemo(() => vocab.filter((v) => v.kind === 'number'), [vocab])
  const dates = useMemo(() => vocab.filter((v) => v.kind === 'date'), [vocab])
  const flags = useMemo(() => vocab.filter((v) => v.kind === 'flag'), [vocab])

  const add = (name: string) => {
    const entry = vocab.find((v) => v.name === name)
    if (!entry) return
    onAdd(
      entry.kind === 'band'
        ? { kind: 'band', name, op: '==', values: [] }
        : entry.kind === 'date'
          // `>=` first: a range is nearly always built lower bound first, and
          // "on or after" is the half people type when they only want one.
          ? { kind: 'date', name, op: '>=', on: null }
        : entry.kind === 'number'
          ? {
              kind: 'number',
              name,
              // The direction people mean by "filter for a week": within it.
              // Counts default the other way — "more than N" is the question
              // asked of a visit count far more often than "fewer than".
              op: entry.measure === 'count' ? '>' : '<=',
              bound: null,
            }
          : { kind: 'flag', name, negated: false },
    )
    setChoice('')
  }

  return (
    <div className="row" style={{ gap: 8 }}>
      <select
        value={choice}
        disabled={disabled}
        style={{ minWidth: 280 }}
        onChange={(e) => add(e.target.value)}
      >
        <option value="">+ Add a requirement…</option>
        <optgroup label="Bands — a classification, compared">
          {bands.map((v) => (
            <option key={v.name} value={v.name} disabled={used.includes(v.name)}>
              {v.name}
              {used.includes(v.name) ? ' (already used)' : ''}
            </option>
          ))}
        </optgroup>
        <optgroup label="Dates and counts — compared to a number you choose">
          {numbers.map((v) => (
            <option key={v.name} value={v.name} disabled={used.includes(v.name)}>
              {v.name}
              {v.windowed ? ' (needs a window)' : ''}
              {used.includes(v.name) ? ' (already used)' : ''}
            </option>
          ))}
        </optgroup>
        <optgroup label="Dates — a fixed point on the calendar">
          {dates.map((v) => (
            <option key={v.name} value={v.name}>
              {v.name}
            </option>
          ))}
        </optgroup>
        <optgroup label="Flags — a yes/no fact">
          {flags.map((v) => (
            <option key={v.name} value={v.name} disabled={used.includes(v.name)}>
              {v.name}
              {used.includes(v.name) ? ' (already used)' : ''}
            </option>
          ))}
        </optgroup>
      </select>
      <span className="hint">
        {vocab.length} names — everything <span className="mono">derive()</span> produces, and
        nothing it does not.
      </span>
    </div>
  )
}

/**
 * What the condition asks for, and the expression it becomes.
 *
 * Requirements as a list rather than a sentence, because that is what a flat
 * `&&` chain IS — the same reading `explain.clauses()` gives it server-side.
 * The expression is below, secondary: it is what gets saved, but it is not what
 * anybody is deciding about.
 */
function Readback({
  clauses,
  vocab,
  catchAll,
}: {
  clauses: Clause[]
  vocab: VocabularyEntry[]
  catchAll: boolean
}) {
  const when = serialize(clauses)
  return (
    <div className="readback">
      {clauses.length === 0 ? (
        <p className="hint" style={{ margin: 0 }}>
          {catchAll
            ? 'Matches whatever every filter above it left — that is what a catch-all is for.'
            : 'No requirements, so this matches every account in the mailable base that no ' +
              'earlier filter took first. Almost certainly not what you want unless this is ' +
              'the catch-all.'}
        </p>
      ) : (
        <>
          <div className="hint label-line">
            An account is in this segment when{' '}
            {clauses.length === 1 ? 'this is true' : `all ${clauses.length} of these are true`} —
            and no filter above it matched first:
          </div>
          <ul className="bullets tight">
            {clauses.map((c) => (
              <li key={serializeClause(c)} style={{ fontSize: 12.5 }}>
                {readClause(c, vocab.find((v) => v.name === c.name))}
              </li>
            ))}
          </ul>
        </>
      )}
      <div className="cond mono" style={{ marginTop: 'var(--s2)', marginBottom: 0 }}>
        {when || '(no condition)'}
      </div>
    </div>
  )
}
